import get from 'lodash/get';
import isNil from 'lodash/isNil';

// Namespace
import * as ProposalNS from './Proposal';
import { DataPoints } from './DAOs/DataPoints';

// Utils
import { ASSET_ALLOCATION_GROUPS, COMPARISON_ITEM_TYPES, YieldParamsType, BOOLEAN_OPERATOR } from '@/utils/constants';
import { deepClone } from '@/utils/objects';
import { userHasPermissions, userHasPermission } from '@/utils/permissions';
import { FALLBACK_COLOR, PROPOSAL_ITEM_PURPOSE, PROPOSAL_ORIENTATION } from './utils/constants';
import { LOCALE, formatLocalizedString, formatMessageWithLocale } from '@/utils/i18n';
import { isGICBenchmark } from '@/utils/bmo-gif/utils';
import { isFFEnabled } from '@/utils/feature-flag';
import { getPortfoliosForView } from './utils/client-level-summary';
import { proposalHasAnyAccountSetWithSelectedPortfolios } from './utils/executive-summary';

// Types
import User from '@/types/user';
import ComparisonItem from '@/types/comparison-item';
import RiskProfileItem from '@/types/risk-profile-item';
import ProposalOrientation from '@/types/proposal-orientation';
import NavigationItem from '@/types/navigation-item';
import { CLIENT_LEVEL_VIEW } from '@/enums/ClientLevelView';

export const validateRiskProfileBuildRules = (proposal: ProposalNS.Proposal, buildRule: ProposalNS.RiskProfileRule) => {
  const hasItemWithRiskProfile = proposal.items.some(item => item.riskProfile !== undefined);

  const noRiskProfiles = proposal.riskProfile === undefined && !hasItemWithRiskProfile;
  const invalidComparisonRiskProfileBuild = buildRule.useComparisonRiskProfile && proposal.riskProfile === undefined;
  const invalidPortfolioRiskProfileBuild = buildRule.usePortfolioRiskProfile && proposal.riskProfile !== undefined;

  if (noRiskProfiles || invalidComparisonRiskProfileBuild || invalidPortfolioRiskProfileBuild) {
    return false;
  }

  return true;
};

export const shouldBuildElement = (
  element: ProposalNS.ProposalTemplateElement,
  proposal: ProposalNS.Proposal,
  user: User,
  pageOverflowIndex: number = 0,
): boolean => {
  const { buildRules } = element;
  const { customParameters } = proposal.template;
  if (buildRules === undefined || buildRules.length === 0) {
    return true;
  }
  const singleItemShift = customParameters && customParameters.singleItemShift && proposal.items.length === 1;
  let proposalIndexes: number[] = proposal.items.map((item, index) => index);
  if (singleItemShift) {
    proposalIndexes = [1];
  }

  for (const buildRule of buildRules) {
    switch (buildRule.type) {
      case ProposalNS.ElementBuildRuleTypes.RiskProfile: {
        if (!validateRiskProfileBuildRules(proposal, buildRule)) {
          return false;
        }

        break;
      }
      case ProposalNS.ElementBuildRuleTypes.Permission: {
        if (buildRule.operator === 'NOT') {
          // Build only if the user does NOT have the permissions
          return !userHasPermissions(user, buildRule.permissions);
        }
        if (buildRule.operator === 'OR') {
          // Build if the user has at least one of the permissions
          if (!buildRule.permissions.some(permission => userHasPermission(user, permission))) {
            return false;
          }
        } else if (!userHasPermissions(user, buildRule.permissions)) {
          // Default is AND, meaning user must have all permissions
          return false;
        }
        break;
      }
      case ProposalNS.ElementBuildRuleTypes.FeatureFlag: {
        if (buildRule.operator === 'OR') {
          // Build if at least one feature flag is enabled
          if (!buildRule.flags.some(flag => isFFEnabled(flag))) {
            return false;
          }
        } else if (!buildRule.flags.every(flag => isFFEnabled(flag))) {
          // Default is AND, meaning all feature flags must be enabled
          return false;
        }
        break;
      }
      case ProposalNS.ElementBuildRuleTypes.AssetAllocation: {
        for (const index of buildRule.indexes || []) {
          const effectiveIndex = singleItemShift ? 0 : proposalIndexes.indexOf(index);
          if (effectiveIndex === -1) {
            return false;
          }
          const allocationAmount = DataPoints.getAssetAllocationTotalsByCategory(
            proposal.items[effectiveIndex],
            ASSET_ALLOCATION_GROUPS[buildRule.allocationType],
          ).response;
          if (buildRule.minAllocation && allocationAmount < buildRule.minAllocation) {
            return false;
          }
          if (allocationAmount === 0) {
            return false;
          }
        }
        break;
      }
      case ProposalNS.ElementBuildRuleTypes.Item: {
        const hasAllItems = (buildRule.indexes || []).some(buildIndex => proposalIndexes.indexOf(buildIndex) >= 0);
        if (!hasAllItems) {
          return false;
        }
        break;
      }
      case ProposalNS.ElementBuildRuleTypes.PageOverflowIndex: {
        if (buildRule.firstPageOnly && pageOverflowIndex > 0) {
          return false;
        }
        break;
      }
      case ProposalNS.ElementBuildRuleTypes.ItemType: {
        const hasItemOfTypes = proposal.items.some(item => buildRule.itemTypes.some(itemType => itemType === item.type));
        if (!hasItemOfTypes) {
          return false;
        }
        break;
      }
      case ProposalNS.ElementBuildRuleTypes.ItemProperty: {
        const hasItemWithProperty = proposal.items.some(item => !isNil(get(item, buildRule.property)));
        if (!hasItemWithProperty) {
          return false;
        }
        break;
      }
      case ProposalNS.ElementBuildRuleTypes.ProposalProperty: {
        const value = get(proposal, buildRule.property);

        if (value == null) {
          return false;
        }

        break;
      }
      case ProposalNS.ElementBuildRuleTypes.IncludeProfiles: {
        const { includeFundProfiles, includePortfolioProfiles, includeGroupProfiles } = proposal.options;
        switch (buildRule.profileType) {
          case ProposalNS.ItemProfileType.Fund: {
            return includeFundProfiles;
          }
          case ProposalNS.ItemProfileType.Portfolio: {
            return includePortfolioProfiles;
          }
          case ProposalNS.ItemProfileType.Group: {
            return includeGroupProfiles;
          }
        }
      }
      case ProposalNS.ElementBuildRuleTypes.Orientation: {
        const { orientation } = proposal.options;
        if (orientation !== buildRule.orientation) {
          return false;
        }
        break;
      }
      case ProposalNS.ElementBuildRuleTypes.ItemPurpose: {
        const hasItemWithPurpose = proposal.items.some(item => item.flags?.includes(buildRule.purpose));
        if (!hasItemWithPurpose) {
          return false;
        }
        break;
      }
      case ProposalNS.ElementBuildRuleTypes.GIFItems: {
        if (!proposal.gifItems || proposal.gifItems.length === 0) {
          return false;
        }

        break;
      }
      case ProposalNS.ElementBuildRuleTypes.AccountSetPortfolios: {
        if (buildRule.view === CLIENT_LEVEL_VIEW.MIXED) {
          if (!proposalHasAnyAccountSetWithSelectedPortfolios(proposal.accountSets, proposal.items)) {
            return false;
          }
        } else {
          const portfolios = getPortfoliosForView(proposal, buildRule.view);
          if (portfolios.length === 0) {
            return false;
          }
        }

        break;
      }
    }
  }

  return true;
};

export const validateDisableRules = (
  disableRules: ProposalNS.ElementDisableRule[],
  selectedSections: string[],
  items: ComparisonItem[],
  riskProfile?: RiskProfileItem,
): { isDisabled: boolean; sidebarTooltip?: ProposalNS.SidebarTooltip } => {
  let isDisabled = false;
  let sidebarTooltip;

  if (disableRules) {
    for (const rule of disableRules) {
      switch (rule.type) {
        case ProposalNS.ElementDisableRuleTypes.DependentSection: {
          if (rule.operator === BOOLEAN_OPERATOR.OR) {
            const hasDependentSelected = rule.sections.some(ruleSection => selectedSections.some(selectedSection => selectedSection === ruleSection));

            if (!hasDependentSelected) {
              isDisabled = true;
            }
          }
          break;
        }
        case ProposalNS.ElementDisableRuleTypes.SelectedSection: {
          if (rule.operator === BOOLEAN_OPERATOR.OR) {
            isDisabled = rule.sections.some(ruleSection => selectedSections.includes(ruleSection));
          }

          if (rule.operator === BOOLEAN_OPERATOR.AND) {
            isDisabled = rule.sections.every(ruleSection => selectedSections.includes(ruleSection));
          }
          break;
        }
        case ProposalNS.ElementDisableRuleTypes.RiskProfile: {
          if (rule.requireAnswers) {
            if (!riskProfile?.savedSections || Object.keys(riskProfile?.savedSections).length === 0) {
              isDisabled = true;
            }
          }
          break;
        }
        case ProposalNS.ElementDisableRuleTypes.MinItems: {
          if (items.filter(item => item.type === rule.itemType).length < rule.value) {
            isDisabled = true;
            sidebarTooltip = rule.sidebarTooltip;
          }

          break;
        }
        case ProposalNS.ElementDisableRuleTypes.ItemPurposeCurrentRecommended: {
          const eligibleItems = items.filter(item => item.flags?.some(flag => Object.values<string>(PROPOSAL_ITEM_PURPOSE).includes(flag)));

          const currentItem = eligibleItems.some(item => item.flags?.includes(PROPOSAL_ITEM_PURPOSE.CURRENT));
          const recommendedItems = eligibleItems.some(item => item.flags?.includes(PROPOSAL_ITEM_PURPOSE.RECOMMENDED));

          if (!currentItem || !recommendedItems) {
            isDisabled = true;
            sidebarTooltip = rule.sidebarTooltip;
          }

          break;
        }
        case ProposalNS.ElementDisableRuleTypes.ItemPurposeItemType: {
          const ineligibleItems = items.filter(
            item => !rule.itemTypes.includes(item.type) && item.flags?.some(flag => Object.values<string>(PROPOSAL_ITEM_PURPOSE).includes(flag)),
          );

          if (ineligibleItems.length !== 0) {
            isDisabled = true;
            sidebarTooltip = rule.sidebarTooltip;
          }
          break;
        }
        case ProposalNS.ElementDisableRuleTypes.ItemProperty: {
          const hasItemWithProperty = items.some(item => !isNil(get(item, rule.property)));
          if (!hasItemWithProperty) {
            isDisabled = true;
            sidebarTooltip = rule.sidebarTooltip;
          }
          break;
        }
      }
    }
  }

  return {
    isDisabled,
    sidebarTooltip,
  };
};

export const getMaxNumberOfItems = (template: ProposalNS.ProposalTemplateV2, orientation: ProposalOrientation): number => {
  const selectedOrientation = template.availableOrientations.find(availableOrientation => availableOrientation.orientation === orientation);
  if (selectedOrientation !== undefined) {
    return selectedOrientation.maxItemsSelected;
  }

  // if we are here, we need a default.
  return 4;
};

export const getDefaultOrientation = (template: ProposalNS.ProposalTemplateV2, proposalItemCount: number): ProposalOrientation => {
  // makes no sense, but it could happen
  if (template.availableOrientations.length === 0) {
    return PROPOSAL_ORIENTATION.PORTRAIT;
  }

  // take the first orientation as the default one. If it is first.. it may be the "preferred" one.
  const defaultOrientation: ProposalOrientation = template.availableOrientations[0].orientation;

  // sort be increasing size to pick the smallest one that will fit.
  const sorted: ProposalNS.ProposalAvailableOrientation[] = deepClone(template.availableOrientations).sort((a, b) => a.maxItemsSelected - b.maxItemsSelected);

  // sorted by size, take the first one that fits
  for (const availableOrientations of sorted) {
    if (availableOrientations.maxItemsSelected >= proposalItemCount) {
      return availableOrientations.orientation;
    }
  }

  // everything was too small take the bigger one available.
  if (sorted.length > 0) {
    return sorted[sorted.length - 1].orientation;
  }

  return defaultOrientation;
};

export const getProposalTemplateDivisionKeys = (template: ProposalNS.ProposalTemplateV2) => {
  const keys = [];
  for (const division of template.root) {
    keys.push(division.key);
  }
  return keys;
};

export const getProposalTemplateSectionKeys = (template: ProposalNS.ProposalTemplateV2) => {
  const keys = [];
  for (const templateDivision of template.root) {
    for (const section of templateDivision.sections) {
      keys.push(section.key);
    }
  }
  return keys;
};

export const isValidGICItem = (item: ComparisonItem, minRate?: number, maxRate?: number) => {
  if (item.type === COMPARISON_ITEM_TYPES.PORTFOLIO && item.holdings.length === 1 && item.holdings[0].weight === 100) {
    const { yieldParams } = item.holdings[0];

    if (yieldParams !== undefined) {
      const isGIC = yieldParams.type === YieldParamsType.GIC;
      const validMinRate = minRate !== undefined ? yieldParams.rate >= minRate : true;
      const validMaxRate = maxRate !== undefined ? yieldParams.rate <= maxRate : true;

      return isGIC && validMinRate && validMaxRate;
    }

    if (yieldParams === undefined && isGICBenchmark(item)) {
      return true;
    }
  }

  return false;
};

export const itemSelectorFiltersContainsGIC = (filters?: ProposalNS.ProposalItemSelectionFilter[]): boolean =>
  !!filters &&
  filters.some(filter => {
    switch (filter.type) {
      // If filter type is GIC, we found what we're looking for.
      case ProposalNS.ProposalItemSelectionFilterType.GIC:
        return true;
      // If filter type is "or condition", look inside it's own condition filters.
      case ProposalNS.ProposalItemSelectionFilterType.OrCondition:
        return itemSelectorFiltersContainsGIC(filter.conditions);
      // Else, there's nothing to be found.
      default:
        return false;
    }
  });

export const updateGICItemNames = (items: ComparisonItem[]) => {
  const clonedItems = items.map(deepClone);

  for (const item of clonedItems) {
    const gicBenchmark = isGICBenchmark(item);

    if (gicBenchmark && item.holdings[0].name) {
      item.name = item.holdings[0].name;
    }

    if (isValidGICItem(item) && !gicBenchmark) {
      const { yieldParams } = item.holdings[0];
      const rate = yieldParams !== undefined ? yieldParams.rate : 0;

      item.name = {
        [LOCALE.EN_CA]: `${formatMessageWithLocale('Common.GIC', LOCALE.EN_CA)} - ${rate}%`,
        [LOCALE.FR_CA]: `${formatMessageWithLocale('Common.GIC', LOCALE.FR_CA)} - ${rate}%`,
      };
    }
  }

  return clonedItems;
};

export const validateItemUniverse = (item: ComparisonItem, universe: ProposalNS.ProposalItemSelectionUniverse): boolean => {
  switch (universe) {
    case ProposalNS.ProposalItemSelectionUniverse.InsuranceFunds: {
      return !!item.universe && item.universe['en-CA'] === 'Insurance Funds';
    }
    default: {
      return false;
    }
  }
};

export const evaluateItemFilter = (item: ComparisonItem, filter: ProposalNS.ProposalItemSelectionFilter): boolean => {
  switch (filter.type) {
    case ProposalNS.ProposalItemSelectionFilterType.OrCondition: {
      return filter.conditions.some(condition => evaluateItemFilter(item, condition));
    }

    case ProposalNS.ProposalItemSelectionFilterType.ItemType: {
      return item.type === filter.value;
    }

    case ProposalNS.ProposalItemSelectionFilterType.GIC: {
      return isValidGICItem(item, filter.minRate, filter.maxRate);
    }

    case ProposalNS.ProposalItemSelectionFilterType.Universe: {
      return validateItemUniverse(item, filter.value);
    }

    case ProposalNS.ProposalItemSelectionFilterType.Manufacturer: {
      return item.manufacturer === filter.value;
    }

    default:
      return false;
  }
};

export const filterItemsByItemSelectionFilters = (items: ComparisonItem[], filters: ProposalNS.ProposalItemSelectionFilter[]) =>
  items.slice().filter(item => filters.every(filter => evaluateItemFilter(item, filter)));

export const validateAreaFillCondition = (item: ComparisonItem, conditions: ProposalNS.AreaFillConditions[]) =>
  conditions.some(condition => {
    switch (condition.type) {
      case ProposalNS.AreaFillConditionType.ItemType: {
        return condition.value.includes(item.type);
      }

      default:
        return false;
    }
  });

export const runBuildRules = (buildRules: ProposalNS.ElementBuildRule[], proposal: ProposalNS.Proposal, user: any): boolean => {
  if (buildRules !== undefined) {
    for (const rule of buildRules) {
      switch (rule.type) {
        case ProposalNS.ElementBuildRuleTypes.ItemType: {
          const hasItemOfTypes = proposal.items.some(item => rule.itemTypes.some(itemType => itemType === item.type));
          if (!hasItemOfTypes) {
            return false;
          }
          break;
        }
        case ProposalNS.ElementBuildRuleTypes.ItemProperty: {
          const hasItemWithProperty = proposal.items.some(item => Object.prototype.hasOwnProperty.call(item, rule.property));
          if (!hasItemWithProperty) {
            return false;
          }
          break;
        }
        case ProposalNS.ElementBuildRuleTypes.ProposalProperty: {
          const value = get(proposal, rule.property);

          if (value == null) {
            return false;
          }

          break;
        }
        case ProposalNS.ElementBuildRuleTypes.RiskProfile: {
          if (!validateRiskProfileBuildRules(proposal, rule)) {
            return false;
          }

          break;
        }
        case ProposalNS.ElementBuildRuleTypes.Permission: {
          if (rule.operator !== undefined && rule.operator === 'OR') {
            if (!rule.permissions.some(permission => userHasPermission(user, permission))) {
              return false;
            }
          } else if (!userHasPermissions(user, rule.permissions)) {
            return false;
          }
          break;
        }
        case ProposalNS.ElementBuildRuleTypes.FeatureFlag: {
          if (rule.operator === 'OR') {
            if (!rule.flags.some(flag => isFFEnabled(flag))) {
              return false;
            }
          } else if (!rule.flags.every(flag => isFFEnabled(flag))) {
            return false;
          }
          break;
        }
        case ProposalNS.ElementBuildRuleTypes.Orientation: {
          const { orientation } = proposal.options;
          if (orientation !== rule.orientation) {
            return false;
          }
          break;
        }
        case ProposalNS.ElementBuildRuleTypes.ItemPurpose: {
          const hasItemWithPurpose = proposal.items.some(item => item.flags?.includes(rule.purpose));
          if (!hasItemWithPurpose) {
            return false;
          }
          break;
        }
        case ProposalNS.ElementBuildRuleTypes.GIFItems: {
          if (!proposal.gifItems || proposal.gifItems.length === 0) {
            return false;
          }
          break;
        }
        case ProposalNS.ElementBuildRuleTypes.AccountSetPortfolios: {
          if (rule.view === undefined) {
            if (!proposalHasAnyAccountSetWithSelectedPortfolios(proposal.accountSets, proposal.items)) {
              return false;
            }
          } else {
            const portfolios = getPortfoliosForView(proposal, rule.view);
            if (portfolios.length === 0) {
              return false;
            }
          }
          break;
        }
      }
    }
  }

  return true;
};

export const getSidebarNavigationTabs = (user: User, proposalTemplate: ProposalNS.ProposalTemplateV2): NavigationItem[] => {
  const currentTabs: NavigationItem[] = [];
  if (!proposalTemplate || proposalTemplate.sidebarTabs.length === 0) {
    return currentTabs;
  }
  const filterSidebarElements = (element: ProposalNS.ProposalSidebarElement) =>
    element.permissions === undefined || (element.permissions !== undefined && userHasPermissions(user, element.permissions));
  const filteredSidebarTabs = proposalTemplate.sidebarTabs
    .map(tab => {
      const filteredElements = tab.elements.slice().filter(filterSidebarElements);

      return { ...tab, elements: filteredElements };
    })
    .filter(tab => tab.elements.length > 0);

  filteredSidebarTabs.forEach(tab => {
    currentTabs.push({
      id: tab.key,
      label: formatLocalizedString(tab.label),
    });
  });

  return currentTabs;
};

export const getTabElements = (sidebarToDisplay: ProposalNS.ProposalSidebarTab | undefined, user: User) => {
  if (!sidebarToDisplay) return [];
  return sidebarToDisplay.elements.filter(
    (element: ProposalNS.ProposalSidebarElement) =>
      element.permissions === undefined || (element.permissions !== undefined && userHasPermissions(user, element.permissions)),
  );
};

export const getProposalCoBrandingColor = (user: User, styleOptions: ProposalNS.ProposalStyleOptionsV2) => {
  const defaultColor = user.coBranding?.colors?.primary;
  return styleOptions.banners.coBrandingColors?.primary || defaultColor || FALLBACK_COLOR;
};
